import React, { HTMLProps, useLayoutEffect, useRef, useState } from "react";
import "./ShiftTable.css";
import { StickyTable, Row, Cell } from "./StickyTable";

export type Shift = {
  workplaceUserId: string;
  name: string;
  hexColor: string;
  date: Date;
};

export type WorkplaceUser = {
  id: string;
  name: string;
  order: number;
};

export type Workplace = {
  name: string;
};

/// https://zenn.dev/uhyo/articles/array-n-keys-yamero#ではどうするのが良いか
function getMaxDayOfMonth(year: number, month: number): number {
  const date = new Date(year, month, 0);
  return date.getDate();
}

export type ShiftTableProps = HTMLProps<void> & {
  year: number;
  month: number;
  shifts: Shift[];
  workplaceUsers: WorkplaceUser[];
};

/**
 * Returns an iterator that iterates integers in [start, end).
 */
function* range(start: number, end: number) {
  for (let i = start; i < end; i++) {
    yield i;
  }
}

function isSameDay(lhs: Date, rhs: Date): boolean {
  return (
    lhs.getFullYear() === rhs.getFullYear() &&
    lhs.getMonth() === rhs.getMonth() &&
    lhs.getDate() === rhs.getDate()
  );
}

type FittedCellTextProps = {
  children?: string;
  baseFontSize: number;
  lineHeight?: number;
  minFontSize?: number;
  maxLines?: number;
};

const FittedCellText: React.FC<FittedCellTextProps> = ({
  children,
  baseFontSize,
  lineHeight = 1,
  minFontSize = 10,
  maxLines = 2,
}) => {
  const textRef = useRef<HTMLSpanElement>(null);
  const [fontSize, setFontSize] = useState(`${baseFontSize}px`);

  useLayoutEffect(() => {
    const textElement = textRef.current;

    if (!textElement || !children) {
      setFontSize(`${baseFontSize}px`);
      return;
    }

    let nextFontSize = baseFontSize;
    textElement.style.fontSize = `${nextFontSize}px`;
    textElement.style.maxHeight = `${nextFontSize * lineHeight * maxLines}px`;

    while (
      nextFontSize > minFontSize &&
      textElement.scrollHeight > textElement.clientHeight
    ) {
      nextFontSize -= 0.5;
      textElement.style.fontSize = `${nextFontSize}px`;
      textElement.style.maxHeight = `${nextFontSize * lineHeight * maxLines}px`;
    }

    setFontSize(`${nextFontSize}px`);
  }, [baseFontSize, children, lineHeight, maxLines, minFontSize]);

  if (!children) {
    return null;
  }

  return (
    <span
      ref={textRef}
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: "100%",
        height: "100%",
        maxHeight: `calc(${fontSize} * ${lineHeight} * ${maxLines})`,
        overflow: "hidden",
        whiteSpace: "normal",
        wordBreak: "break-all",
        fontSize,
        lineHeight,
        letterSpacing: 0,
      }}
    >
      {children}
    </span>
  );
};

export const ShiftTable: React.FC<ShiftTableProps> = (props) => {
  const today = new Date();
  const rowHeight = "30px";
  const headerCellPadding = "0.125rem";

  return (
    <StickyTable borderWidth="1px" borderColor="#dfe1e6">
      <Row>
        <Cell />
        {props.workplaceUsers.map((user) => (
          <Cell
            key={user.name}
            style={{
              width: "5rem",
              minWidth: "5rem",
              maxWidth: "5rem",
              textAlign: "center",
              borderRight: "1px solid #dfe1e6",
              fontWeight: "600",
              backgroundColor: "#f4f5f7",
              height: rowHeight,
              maxHeight: rowHeight,
              padding: headerCellPadding,
              verticalAlign: "middle",
            }}
          >
            <FittedCellText baseFontSize={15} lineHeight={1.1} minFontSize={11}>
              {user.name}
            </FittedCellText>
          </Cell>
        ))}
      </Row>
      {[...range(1, getMaxDayOfMonth(props.year, props.month) + 1)].map(
        (day) => {
          const date = new Date(props.year, props.month - 1, day, 0, 0, 0, 0);

          return (
            <Row key={date.toString()}>
              <Cell
                key={day}
                style={{
                  backgroundColor: "#fafbfc",
                  textAlign: "right",
                  fontFamily: "monospace",
                  fontWeight: "600",
                  fontSize: "0.875rem",
                  height: rowHeight,
                  maxHeight: rowHeight,
                  padding: "0.4rem",
                  verticalAlign: "middle",
                  color: date.getDay() === 0
                    ? "#de350b"
                    : date.getDay() === 6
                    ? "#0052cc"
                    : "#172b4d",
                  minWidth: "5rem",
                  width: "5rem",
                  maxWidth: "5rem",
                  borderLeft: isSameDay(today, date) ? "6px solid #de350b" : undefined,
                  paddingLeft: isSameDay(today, date) ? "calc(0.4rem - 2px)" : "0.4rem",
                }}
              >
                {day}{" "}
                {date.toLocaleString(window.navigator.language, {
                  weekday: "short",
                })}
              </Cell>
              {props.workplaceUsers.map((user) => {
                const shift = props.shifts.filter((shift) => {
                  return (
                    isSameDay(date, shift.date) &&
                    shift.workplaceUserId === user.id
                  );
                })[0];

                return (
                  <Cell
                    key={`${day}-${user.name}`}
                    style={{
                      backgroundColor: shift?.hexColor
                        ? `#${shift?.hexColor}`
                        : "#ffffff",
                      textAlign: "center",
                      overflow: "hidden",
                      wordBreak: "break-all",
                      fontWeight: shift?.name ? "600" : "normal",
                      width: "5rem",
                      maxWidth: "5rem",
                      minWidth: "5rem",
                      minHeight: rowHeight,
                      height: rowHeight,
                      maxHeight: rowHeight,
                      padding: "0.25rem 0.125rem",
                      borderRight: "1px solid #dfe1e6",
                      verticalAlign: "middle",
                    }}
                  >
                    <FittedCellText baseFontSize={14}>{shift?.name}</FittedCellText>
                  </Cell>
                );
              })}
            </Row>
          );
        }
      )}
    </StickyTable>
  );
};
